// src/components/fuel/TruckCard.js
import React from 'react';
import { Truck, Fuel, Wrench, Edit2, Trash2, Activity, DollarSign } from 'lucide-react';
import { TRUCK_STATUS_CONFIG, FUEL_META } from './constants';
import { fmt, fmtUSD } from './formatters';

const TruckCard = ({ truck, onEdit, onDelete, onRefill, onTransactions, onUpdatePrice }) => {
  const statusConfig = TRUCK_STATUS_CONFIG[truck.status] || TRUCK_STATUS_CONFIG.available;
  const fuelMeta = FUEL_META[truck.fuel_type] || FUEL_META['JetA'];
  const fillPercent = truck.capacity_gallons 
    ? (truck.current_volume_gallons / truck.capacity_gallons) * 100 
    : 0;
  const isLowFuel = fillPercent < 25;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition">
      {/* En-tête */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${fuelMeta.badge}`}>
            <Truck size={20} className={fuelMeta.bar.replace('bg-', 'text-')} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-semibold">{truck.truck_number}</h3>
              {truck.truck_name && (
                <span className="text-gray-400 text-xs">({truck.truck_name})</span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
                {statusConfig.icon} {statusConfig.label}
              </span>
            </div>
            {truck.driver_name && (
              <p className="text-gray-500 text-xs mt-1">
                Driver: {truck.driver_name} {truck.driver_phone && `· ${truck.driver_phone}`}
              </p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Price: <span className="text-green-400 font-medium">{fmtUSD(truck.price_per_gallon || 6.85)}/gal</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => onUpdatePrice && onUpdatePrice(truck)} 
            className="text-green-400 hover:text-green-300 transition p-1" 
            title="Update Price">
            <DollarSign size={16} />
          </button>
          <button 
            onClick={() => onTransactions(truck)} 
            className="text-purple-400 hover:text-purple-300 transition p-1" 
            title="View Transactions">
            <Activity size={16} />
          </button>
          <button 
            onClick={() => onRefill(truck)} 
            className="text-blue-400 hover:text-blue-300 transition p-1" 
            title="Refill Truck">
            <Fuel size={16} />
          </button>
          <button 
            onClick={() => onEdit(truck)} 
            className="text-yellow-400 hover:text-yellow-300 transition p-1" 
            title="Edit">
            <Edit2 size={16} />
          </button>
          <button 
            onClick={() => onDelete(truck)} 
            className="text-red-400 hover:text-red-300 transition p-1" 
            title="Delete">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Informations techniques */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
        <div className="bg-gray-700/30 rounded-lg p-2">
          <p className="text-gray-500">Capacity</p>
          <p className="text-white font-medium">{fmt(truck.capacity_gallons)} gal</p>
        </div>
        <div className="bg-gray-700/30 rounded-lg p-2">
          <p className="text-gray-500">Fuel Type</p>
          <p className={`text-xs font-medium ${fuelMeta.badge}`}>{fuelMeta.label}</p>
        </div>
        {truck.license_plate && (
          <div className="bg-gray-700/30 rounded-lg p-2">
            <p className="text-gray-500">License Plate</p>
            <p className="text-white text-xs">{truck.license_plate}</p>
          </div>
        )}
        <div className="bg-gray-700/30 rounded-lg p-2">
          <p className="text-gray-500">Current Fuel</p>
          <p className={`font-medium ${isLowFuel ? 'text-yellow-400' : 'text-white'}`}>
            {fmt(truck.current_volume_gallons)} gal
          </p>
        </div>
      </div>

      {/* Barre de niveau de carburant */}
      <div>
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>Fuel Level</span>
          <span className="text-white font-bold">{fmt(fillPercent, 0)}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 rounded-full transition-all duration-700 ${fuelMeta.bar} ${isLowFuel ? 'animate-pulse' : ''}`}
            style={{ width: `${Math.max(2, fillPercent)}%` }}
          />
        </div>
      </div>

      {/* Maintenance info */}
      {truck.next_maintenance_date && (
        <div className="mt-3 pt-2 border-t border-gray-700 text-xs text-gray-500 flex items-center gap-1">
          <Wrench size={10} />
          <span>Next maintenance: {new Date(truck.next_maintenance_date).toLocaleDateString('en-US')}</span>
        </div>
      )}

      {/* Notes */}
      {truck.notes && (
        <div className="mt-2 text-xs text-gray-500 italic">
          📝 {truck.notes.length > 60 ? truck.notes.substring(0, 60) + '...' : truck.notes}
        </div>
      )}
    </div>
  );
};

export default TruckCard;